import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { DxLoadIndicatorModule } from 'devextreme-angular';

/**
 * A loading indicator that sits in a DataGrid/TreeList command column alongside
 * the icon buttons, for an async action started from one of them.
 *
 * DevExtreme column buttons have no loading state of their own — `columns[].buttons`
 * exposes only `icon`, `visible`, `disabled`, `template` and friends — so the usual
 * approach is to render this through a button's `template` and toggle it against
 * the real button with `visible`:
 *
 * ```html
 * <dxi-button icon="pdffile" [visible]="isIdle" [onClick]="handleDownload"></dxi-button>
 * <dxi-button hint="Downloading…" template="loadingTemplate"></dxi-button>
 *
 * <div *dxTemplate="let data of 'loadingTemplate'">
 *   <ag-shr-grid-button-indicator></ag-shr-grid-button-indicator>
 * </div>
 * ```
 *
 * NOTE: `visible` callbacks are evaluated while DevExtreme renders the row and are
 * not reactive, so the row must be repainted (`repaintRows`) when the toggling
 * state changes.
 *
 * The host carries `dx-link dx-link-icon` so the framework gives it the same box
 * as the buttons beside it and resolves its colour for each row state.
 */
@Component({
  selector: 'ag-shr-grid-button-indicator',
  standalone: true,
  imports: [DxLoadIndicatorModule],
  template: `<dx-load-indicator [width]="size" [height]="size" [visible]="true"></dx-load-indicator>`,
  styleUrls: ['./grid-button-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridButtonIndicatorComponent {
  /** Matches DevExtreme's 18px command-column icons; override only to match a custom icon size. */
  @Input() size = 18;

  @HostBinding('class') protected readonly hostClass = 'dx-link dx-link-icon ag-shr-grid-button-indicator';

  /**
   * Sized on the host rather than left to DevExtreme. Its 18px box comes from
   * `.dx-command-edit-with-icons .dx-link`, and a column stops being
   * `-with-icons` as soon as one of its buttons uses a `template` — so relying on
   * it left this element unsized, and an inline host wrapping a block indicator
   * pushed the whole button row onto extra lines.
   */
  @HostBinding('style.width.px') protected get hostWidth(): number {
    return this.size;
  }

  @HostBinding('style.height.px') protected get hostHeight(): number {
    return this.size;
  }

}
